import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.strapinapp.com';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Admin Portal APIs
export const adminAPI = {
  // Dashboard
  getDashboardStats: (params) => api.get('/api/admin/portal/dashboard/stats', { params }),

  // Admin management (SUPER_ADMIN)
  getAdmins: () => api.get('/api/admin/portal/admins'),
  createAdmin: (data) => api.post('/api/admin/portal/admins', data),
  updateAdminRole: (adminId, data) => api.patch(`/api/admin/portal/admins/${adminId}/role`, data),
  resetAdminPassword: (adminId, data) => api.patch(`/api/admin/portal/admins/${adminId}/password`, data),
  
  // Users
  getAllUsers: (params) => api.get('/api/admin/portal/users', { params }),
  getAppVersionStats: () => api.get('/api/admin/portal/users/app-versions/stats'),
  getUserDetails: (userId) => api.get(`/api/admin/portal/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/api/admin/portal/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/api/admin/portal/users/${userId}`),
  restoreUser: (userId) => api.post(`/api/admin/portal/users/${userId}/restore`),
  banUser: (userId, data) => api.post(`/api/admin/portal/users/${userId}/ban`, data),
  getUserNotices: (userId) => api.get(`/api/admin/portal/users/${userId}/notices`),
  sendUserNotice: (userId, data) => api.post(`/api/admin/portal/users/${userId}/notices`, data),
  revokeUserNotice: (userId, noticeId) => api.post(`/api/admin/portal/users/${userId}/notices/${noticeId}/revoke`),
  getAnnouncements: () => api.get('/api/admin/portal/announcements'),
  searchAnnouncementRecipients: (search) => api.get('/api/admin/portal/announcements/recipients', { params: { search } }),
  sendAnnouncement: (data) => api.post('/api/admin/portal/announcements', data),
  revokeAnnouncement: (noticeId) => api.post(`/api/admin/portal/announcements/${noticeId}/revoke`),

  // Self-service deleted account archives
  getDeletedAccounts: (params) =>
    api.get('/api/admin/portal/deleted-accounts', { params }),
  getDeletedAccountById: (id) =>
    api.get(`/api/admin/portal/deleted-accounts/${id}`),
  purgeDeletedAccount: (id, data) =>
    api.post(`/api/admin/portal/deleted-accounts/${id}/purge`, data),
  bulkPurgeDeletedAccounts: (data) =>
    api.post('/api/admin/portal/deleted-accounts/bulk-purge', data),
  getDeletedAccountAuditLogs: (params) =>
    api.get('/api/admin/portal/deleted-accounts/audit-logs', { params }),
  getDeletedAccountAuditLogById: (id) =>
    api.get(`/api/admin/portal/deleted-accounts/audit-logs/${id}`),
  
  // Reports
  getAllReports: (params) => api.get('/api/admin/portal/reports', { params }),
  getReportedUsers: (params) => api.get('/api/admin/portal/reports/users', { params }),
  updateReportStatus: (reportId, data) => api.patch(`/api/admin/portal/reports/${reportId}`, data),

  // Feed / community reports (CommunityReport)
  getFeedReports: (params) => api.get('/api/admin/portal/community/reports', { params }),
  updateFeedReport: (reportId, data) =>
    api.patch(`/api/admin/portal/community/reports/${reportId}`, data),

  // User safety queue (Report.js — separate from ContentReport)
  getSafetyQueue: (params) => api.get('/api/admin/portal/safety/queue', { params }),
  getSafetyReport: (reportId) => api.get(`/api/admin/portal/safety/reports/${reportId}`),
  updateSafetyReport: (reportId, data) =>
    api.patch(`/api/admin/portal/safety/reports/${reportId}`, data),
  
  // Moderation
  getModerationQueue: () => api.get('/api/admin/portal/moderation/queue'),
  
  // Analytics
  getAnalytics: (params) => api.get('/api/admin/portal/analytics', { params }),
  getDemographicsAnalytics: (params) =>
    api.get('/api/admin/portal/analytics/demographics', { params }),

  // User Activity
  getUserActivity: (params) => api.get('/api/admin/portal/user-activity', { params }),
  
  // Contacts
  getAllContacts: (params) => api.get('/api/admin/portal/contacts', { params }),
  updateContactStatus: (contactId, data) => api.patch(`/api/admin/portal/contacts/${contactId}`, data),

  // Subscriptions
  getSubscriptionPlans: () => api.get('/api/subscriptions/plans'),
  
  // Admin Subscription Management
  // Plans
  getAdminSubscriptionPlans: () => api.get('/api/admin/subscriptions/plans'),
  getAdminSubscriptionPlan: (planId) => api.get(`/api/admin/subscriptions/plans/${planId}`),
  createSubscriptionPlan: (data) => api.post('/api/admin/subscriptions/plans', data),
  updateSubscriptionPlan: (planId, data) => api.put(`/api/admin/subscriptions/plans/${planId}`, data),
  deleteSubscriptionPlan: (planId) => api.delete(`/api/admin/subscriptions/plans/${planId}`),
  
  // Features
  addSubscriptionFeature: (planId, data) => api.post(`/api/admin/subscriptions/plans/${planId}/features`, data),
  updateSubscriptionFeature: (planId, featureId, data) => api.put(`/api/admin/subscriptions/plans/${planId}/features/${featureId}`, data),
  removeSubscriptionFeature: (planId, featureId) => api.delete(`/api/admin/subscriptions/plans/${planId}/features/${featureId}`),
  
  // Pricing & Discounts
  updateSubscriptionPricing: (planId, duration, data) => api.put(`/api/admin/subscriptions/plans/${planId}/pricing/${duration}`, data),
  updateSubscriptionDiscount: (planId, duration, data) => api.put(`/api/admin/subscriptions/plans/${planId}/discount/${duration}`, data),
  
  // Global Toggle
  toggleSubscriptions: (enabled) => api.put('/api/admin/subscriptions/global-toggle', { enabled }),
  
  // Verifications
  getPendingVerifications: (params) => api.get('/api/admin/verifications/pending', { params }),
  getVerifiedUsers: (params) =>
    api.get('/api/admin/verifications/pending', {
      params: { ...params, state: 'verified' },
    }),
  getVerificationDetails: (userId) => api.get(`/api/admin/verifications/user/${userId}`),
  approveVerification: (userId, data) => api.post(`/api/admin/verifications/approve/${userId}`, data),
  rejectVerification: (userId, data) => api.post(`/api/admin/verifications/reject/${userId}`, data),
  requireReverification: (userId, data) =>
    api.post(`/api/admin/verifications/require-reverification/${userId}`, data),
  requestVerificationRetry: (userId, data) =>
    api.post(`/api/admin/verifications/request-retry/${userId}`, data),
  setVerificationFraudFlag: (userId, data) =>
    api.post(`/api/admin/verifications/user/${userId}/fraud-flag`, data),
  getVerificationStats: () => api.get('/api/admin/verifications/stats'),
  
  // Photo Verifications
  getPendingPhotoVerifications: (params) => api.get('/api/admin/verifications/photo/pending', { params }),
  approvePhotoVerification: (userId, data) => api.post(`/api/admin/verifications/photo/approve/${userId}`, data),
  rejectPhotoVerification: (userId, data) => api.post(`/api/admin/verifications/photo/reject/${userId}`, data),
  
  // Photo Verifications - All Users with Pictures
  getUsersWithProfilePictures: (params) => api.get('/api/admin/portal/photo-verifications/users', { params }),
  // Note: rejectAndReplacePicture is called directly with FormData in PhotoVerifications component
  removePicture: (data) => api.post('/api/admin/portal/photo-verifications/remove-picture', data),
  approvePicture: (data) => api.post('/api/admin/portal/photo-verifications/approve-picture', data),

  getImpressions: (params) =>
    api.get('/api/admin/portal/analytics/tab-impressions', { params }),

  getPromoCampaigns: (params) =>
    api.get('/api/admin/promo-campaigns', { params }),

  getPromoCampaignStats: (params) =>
    api.get('/api/admin/promo-campaigns/stats', { params }),

  upsertPromoCampaign: (data) =>
    api.put('/api/admin/promo-campaigns', data),

  uploadPromoSlideMedia: (file) => {
    const formData = new FormData();
    formData.append('media', file);
    return api.post('/api/admin/promo-campaigns/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  previewBroadcastPush: (params) =>
    api.get('/api/admin/push/broadcast/preview', { params }),

  sendBroadcastPush: (data) =>
    api.post('/api/admin/push/broadcast', data),

  // Hide from Discovery
  getHiddenUsers: () => api.get('/api/admin/portal/users/hidden-from-discovery'),
  hideUsersFromDiscovery: (data) => api.post('/api/admin/portal/users/hide-from-discovery', data),
  unhideUsersFromDiscovery: (data) => api.post('/api/admin/portal/users/unhide-from-discovery', data),

  // Community Moderation
  getCommunityStats: () => api.get('/api/admin/portal/community/stats'),
  getCommunityPosts: (params) => api.get('/api/admin/portal/community/posts', { params }),
  getCommunityPost: (postId) => api.get(`/api/admin/portal/community/posts/${postId}`),
  removeCommunityPost: (postId, data) => api.post(`/api/admin/portal/community/posts/${postId}/remove`, data),
  restoreCommunityPost: (postId) => api.post(`/api/admin/portal/community/posts/${postId}/restore`),
  permanentlyDeleteCommunityPost: (postId, data) => api.delete(`/api/admin/portal/community/posts/${postId}/permanent`, { data }),
  approveCommunityPostImage: (postId, imageId, data) => api.post(`/api/admin/portal/community/posts/${postId}/images/${imageId}/approve`, data || {}),
  rejectCommunityPostImage: (postId, imageId, data) => api.post(`/api/admin/portal/community/posts/${postId}/images/${imageId}/reject`, data || {}),
  scanCommunityPost: (postId) => api.post(`/api/admin/portal/community/posts/${postId}/scan`),
  getGroupPictureModeration: () => api.get('/api/admin/portal/community/group-pictures'),
  approveGroupPicture: (communityId) => api.post(`/api/admin/portal/community/group-pictures/${communityId}/approve`),
  rejectGroupPicture: (communityId, data) => api.post(`/api/admin/portal/community/group-pictures/${communityId}/reject`, data || {}),
  testCommunityModerationImage: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('media', file));
    return api.post('/api/admin/portal/community/moderation/test-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Group Tracker (public/private group creation tracking + admin group creation)
  getGroupStats: () => api.get('/api/admin/portal/groups/stats'),
  getGroups: (params) => api.get('/api/admin/portal/groups', { params }),
  createGroup: (data) => api.post('/api/admin/portal/groups', data),
  uploadGroupImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/api/admin/portal/groups/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

};

export default api;
